import { useState, useEffect, useMemo, useCallback } from "react";

// ─── Supabase Config ────────────────────────────────────────────
const SUPABASE_URL = "https://qfevyxzrnblxgyvoicph.supabase.co";
const SUPABASE_KEY = "sb_publishable_SrADIhA5sqLnu4B7a0tbig_vHhAb_fD";
const ADMIN_UID = "bcfc790f-d07f-4246-b524-df22df26efa0";

// ─── Mini Supabase Client ───────────────────────────────────────
function createClient(url, key) {
  let token = null;
  const headers = () => ({
    apikey: key,
    "Content-Type": "application/json",
    ...(token
      ? { Authorization: `Bearer ${token}` }
      : { Authorization: `Bearer ${key}` }),
  });
  const rest = (table) => `${url}/rest/v1/${table}`;

  return {
    setToken(t) {
      token = t;
    },
    getToken() {
      return token;
    },
    auth: {
      async signIn(email, password) {
        const r = await fetch(`${url}/auth/v1/token?grant_type=password`, {
          method: "POST",
          headers: { apikey: key, "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const d = await r.json();
        if (d.error)
          throw new Error(d.error_description || d.error || "Login failed");
        token = d.access_token;
        return d;
      },
      async getUser() {
        if (!token) return null;
        const r = await fetch(`${url}/auth/v1/user`, { headers: headers() });
        if (!r.ok) return null;
        return await r.json();
      },
    },
    async select(table, query = "") {
      const r = await fetch(`${rest(table)}?${query}&order=created_at.desc`, {
        headers: headers(),
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || "Select failed");
      }
      return await r.json();
    },
    async selectOrder(table, query = "", order = "created_at.desc") {
      const r = await fetch(`${rest(table)}?${query}&order=${order}`, {
        headers: headers(),
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || "Select failed");
      }
      return await r.json();
    },
    async insert(table, data) {
      const r = await fetch(rest(table), {
        method: "POST",
        headers: { ...headers(), Prefer: "return=representation" },
        body: JSON.stringify(data),
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || "Insert failed");
      }
      return await r.json();
    },
    async update(table, id, data) {
      const r = await fetch(`${rest(table)}?id=eq.${id}`, {
        method: "PATCH",
        headers: { ...headers(), Prefer: "return=representation" },
        body: JSON.stringify(data),
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || "Update failed");
      }
      return await r.json();
    },
    async delete(table, id) {
      const r = await fetch(`${rest(table)}?id=eq.${id}`, {
        method: "DELETE",
        headers: headers(),
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || "Delete failed");
      }
      return true;
    },
  };
}

const sb = createClient(SUPABASE_URL, SUPABASE_KEY);

// ─── Color System (Light Mode) ─────────────────────────────────
const C = {
  bg: "#F5F4F1",
  surface: "#FFFFFF",
  surface2: "#EDECE8",
  border: "rgba(0,0,0,0.07)",
  borderMed: "rgba(0,0,0,0.12)",
  text: "#1A1A1A",
  textSec: "#6B6B6B",
  textDim: "#9A9A9A",
  accent: "#1D4ED8",
  accentLight: "#EEF2FF",
  profit: "#059669",
  profitLight: "#ECFDF5",
  loss: "#DC2626",
  lossLight: "#FEF2F2",
  warn: "#D97706",
  warnLight: "#FFFBEB",
  purple: "#7C3AED",
  purpleLight: "#F5F3FF",
  teal: "#0D9488",
  tealLight: "#F0FDFA",
  coral: "#EA580C",
  coralLight: "#FFF7ED",
};
const font = `'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif`;
const mono = `'JetBrains Mono', 'SF Mono', monospace`;

const catColors = {
  Consulta: { bg: C.accentLight, text: C.accent },
  Medicamento: { bg: C.profitLight, text: C.profit },
  Estudio: { bg: C.purpleLight, text: C.purple },
  Enfermera: { bg: C.tealLight, text: C.teal },
  Seguro: { bg: C.warnLight, text: C.warn },
  Otro: { bg: C.surface2, text: C.textSec },
};

// ─── Helpers ────────────────────────────────────────────────────
const fmt = (n) =>
  "$" +
  Math.abs(Number(n) || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
const fmtDate = (d) =>
  new Date(d + "T12:00:00").toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
  });

// ─── Mobile Styles ──────────────────────────────────────────────
const S = {
  page: {
    fontFamily: font,
    background: C.bg,
    minHeight: "100vh",
    color: C.text,
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 0 80px",
    WebkitFontSmoothing: "antialiased",
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: C.surface,
    borderBottom: `1px solid ${C.border}`,
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: { display: "flex", alignItems: "center", gap: 10 },
  logoIcon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    background: C.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
  },
  logoText: { fontSize: 17, fontWeight: 700, letterSpacing: "-0.02em" },
  logoSub: { fontSize: 11, color: C.textSec },
  badge: (isAdmin) => ({
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 20,
    background: isAdmin ? C.accentLight : C.surface2,
    color: isAdmin ? C.accent : C.textSec,
  }),
  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    maxWidth: 480,
    zIndex: 50,
    background: C.surface,
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    justifyContent: "space-around",
    padding: "6px 0 env(safe-area-inset-bottom, 8px)",
  },
  navBtn: (active) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    padding: "6px 12px",
    border: "none",
    background: "none",
    cursor: "pointer",
    color: active ? C.accent : C.textDim,
    fontSize: 10,
    fontWeight: active ? 600 : 400,
    fontFamily: font,
  }),
  navIcon: { fontSize: 20, lineHeight: 1 },
  content: { padding: "16px 16px 0" },
  section: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: C.textSec,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 10,
    padding: "0 2px",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 20,
  },
  kpi: (color, bgColor) => ({
    background: bgColor,
    borderRadius: 14,
    padding: "14px 14px 12px",
    border: `1px solid ${color}12`,
  }),
  kpiLabel: {
    fontSize: 11,
    color: C.textSec,
    fontWeight: 500,
    marginBottom: 2,
  },
  kpiValue: (color) => ({
    fontSize: 22,
    fontWeight: 700,
    color,
    fontFamily: mono,
    letterSpacing: "-0.02em",
  }),
  kpiSub: { fontSize: 11, color: C.textDim, marginTop: 1 },
  card: {
    background: C.surface,
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    marginBottom: 10,
    overflow: "hidden",
  },
  cardInner: { padding: "14px 16px" },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  pill: (bg, text) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: bg,
    color: text,
  }),
  avatar: (color, size = 34) => ({
    width: size,
    height: size,
    borderRadius: "50%",
    background: color + "15",
    color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: size * 0.38,
    fontWeight: 700,
    flexShrink: 0,
  }),
  timeChip: {
    display: "inline-flex",
    padding: "3px 8px",
    borderRadius: 6,
    background: C.accentLight,
    fontSize: 12,
    fontFamily: mono,
    color: C.accent,
    fontWeight: 600,
    marginRight: 4,
    marginBottom: 4,
  },
  stockBar: {
    width: 50,
    height: 5,
    borderRadius: 3,
    background: C.surface2,
    overflow: "hidden",
  },
  alertCard: {
    background: C.warnLight,
    borderRadius: 14,
    padding: "12px 14px",
    border: `1px solid ${C.warn}20`,
    marginBottom: 10,
  },
  movIcon: (type) => ({
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    flexShrink: 0,
    background: type === "entrada" ? C.profitLight : C.lossLight,
    color: type === "entrada" ? C.profit : C.loss,
  }),
  btn: (bg, color) => ({
    padding: "12px 20px",
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: font,
    background: bg,
    color,
    width: "100%",
    transition: "opacity 0.15s",
  }),
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${C.borderMed}`,
    fontSize: 14,
    fontFamily: font,
    outline: "none",
    background: C.surface,
    color: C.text,
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${C.borderMed}`,
    fontSize: 14,
    fontFamily: font,
    outline: "none",
    background: C.surface,
    color: C.text,
    boxSizing: "border-box",
    appearance: "none",
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: C.textSec,
    marginBottom: 6,
    display: "block",
  },
  formGroup: { marginBottom: 14 },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 100,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  modal: {
    background: C.surface,
    borderRadius: "20px 20px 0 0",
    width: "100%",
    maxWidth: 480,
    maxHeight: "85vh",
    overflow: "auto",
    padding: "20px 16px env(safe-area-inset-bottom, 16px)",
  },
  modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 16 },
  modalClose: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "none",
    border: "none",
    fontSize: 22,
    cursor: "pointer",
    color: C.textDim,
    padding: 4,
  },
};

// ─── Login Screen ───────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setErr("");
    setLoading(true);
    try {
      const data = await sb.auth.signIn(email, pass);
      onLogin(data);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        ...S.page,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "0 24px",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div
          style={{
            ...S.logoIcon,
            width: 56,
            height: 56,
            borderRadius: 16,
            fontSize: 24,
            margin: "0 auto 12px",
          }}
        >
          S
        </div>
        <div
          style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}
        >
          cuidandoasiria
        </div>
        <div style={{ fontSize: 14, color: C.textSec, marginTop: 4 }}>
          Cuidando a Siria
        </div>
      </div>
      <div style={S.formGroup}>
        <input
          style={S.input}
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoCapitalize="none"
        />
      </div>
      <div style={S.formGroup}>
        <input
          style={S.input}
          type="password"
          placeholder="Contraseña"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
        />
      </div>
      {err && (
        <div
          style={{
            color: C.loss,
            fontSize: 13,
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {err}
        </div>
      )}
      <button
        style={S.btn(C.accent, "#fff")}
        onClick={handleLogin}
        disabled={loading}
      >
        {loading ? "Entrando..." : "Entrar"}
      </button>
    </div>
  );
}

// ─── Bottom Sheet Modal ─────────────────────────────────────────
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ position: "relative" }}>
          <div style={S.modalTitle}>{title}</div>
          <button style={S.modalClose} onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── KPI Component ──────────────────────────────────────────────
function KPI({ label, value, sub, color, bgColor }) {
  return (
    <div style={S.kpi(color, bgColor)}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={S.kpiValue(color)}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ─── FAB ────────────────────────────────────────────────────────
function FAB({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: "fixed",
        bottom: 80,
        right: 20,
        width: 52,
        height: 52,
        borderRadius: "50%",
        background: C.accent,
        color: "#fff",
        border: "none",
        fontSize: 26,
        fontWeight: 300,
        cursor: "pointer",
        boxShadow: "0 4px 14px rgba(29,78,216,0.35)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      +
    </button>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────
function OverviewTab({ meds, expenses, caja, siblings }) {
  const saldo = caja.reduce(
    (s, m) =>
      m.type === "entrada" ? s + Number(m.amount) : s - Number(m.amount),
    0
  );
  const now = new Date();
  const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}`;
  const totalMonth = expenses
    .filter((e) => e.date && e.date.startsWith(monthStr))
    .reduce((s, e) => s + Number(e.amount), 0);
  const todayMeds = meds
    .filter((m) => m.active)
    .reduce((a, m) => a + (m.times?.length || 0), 0);
  const lowStock = meds.filter((m) => m.active && m.stock <= m.min_stock);

  return (
    <div style={S.content}>
      <div style={S.kpiGrid}>
        <KPI
          label="Caja chica"
          value={fmt(saldo)}
          sub="Saldo disponible"
          color={C.profit}
          bgColor={C.profitLight}
        />
        <KPI
          label="Gasto del mes"
          value={fmt(totalMonth)}
          sub={now.toLocaleDateString("es-MX", {
            month: "long",
            year: "numeric",
          })}
          color={C.accent}
          bgColor={C.accentLight}
        />
        <KPI
          label="Tomas hoy"
          value={todayMeds}
          sub={`${meds.filter((m) => m.active).length} medicamentos`}
          color={C.teal}
          bgColor={C.tealLight}
        />
        <KPI
          label="Stock bajo"
          value={lowStock.length}
          sub={lowStock.length > 0 ? "Resurtir pronto" : "Todo bien"}
          color={lowStock.length > 0 ? C.warn : C.profit}
          bgColor={lowStock.length > 0 ? C.warnLight : C.profitLight}
        />
      </div>

      {lowStock.length > 0 && (
        <div style={S.alertCard}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.warn,
              marginBottom: 6,
            }}
          >
            Medicamentos por resurtir
          </div>
          {lowStock.map((m) => (
            <div key={m.id} style={{ fontSize: 13, marginBottom: 2 }}>
              <strong>{m.name}</strong> — quedan {m.stock} (mín: {m.min_stock})
            </div>
          ))}
        </div>
      )}

      <div style={S.section}>
        <div style={S.sectionTitle}>Balance familiar</div>
        {(() => {
          // Total de todo: gastos de caja + gastos directos
          const totalExpenses = expenses.reduce(
            (s, e) => s + Number(e.amount),
            0
          );
          const cuotaJusta =
            siblings.length > 0 ? totalExpenses / siblings.length : 0;

          // Lo que cada hermano ha pagado = aportaciones a caja + gastos directos
          const balances = siblings.map((s) => {
            const aportaciones = caja
              .filter((m) => m.type === "entrada" && m.sibling_id === s.id)
              .reduce((sum, m) => sum + Number(m.amount), 0);
            const directos = expenses
              .filter((e) => e.source === "directo" && e.paid_by === s.id)
              .reduce((sum, e) => sum + Number(e.amount), 0);
            const pagado = aportaciones + directos;
            const balance = pagado - cuotaJusta;
            return { ...s, aportaciones, directos, pagado, balance };
          });

          // Sort: los que deben primero (más negativo arriba)
          balances.sort((a, b) => a.balance - b.balance);

          return (
            <>
              <div style={{ ...S.card, padding: "12px 16px", marginBottom: 8 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, color: C.textSec }}>
                      Gasto total
                    </div>
                    <div
                      style={{
                        fontFamily: mono,
                        fontSize: 18,
                        fontWeight: 700,
                      }}
                    >
                      {fmt(totalExpenses)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, color: C.textSec }}>
                      Cuota justa ÷ {siblings.length}
                    </div>
                    <div
                      style={{
                        fontFamily: mono,
                        fontSize: 18,
                        fontWeight: 700,
                        color: C.accent,
                      }}
                    >
                      {fmt(Math.round(cuotaJusta))}
                    </div>
                  </div>
                </div>
              </div>
              <div style={S.card}>
                {balances.map((s) => {
                  const isPositive = s.balance >= 0;
                  return (
                    <div key={s.id} style={S.listItem}>
                      <div style={S.avatar(s.color, 34)}>{s.name[0]}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 2,
                          }}
                        >
                          <span style={{ fontSize: 14, fontWeight: 600 }}>
                            {s.name}
                          </span>
                          <span
                            style={{
                              fontFamily: mono,
                              fontSize: 14,
                              fontWeight: 700,
                              color: isPositive ? C.profit : C.loss,
                            }}
                          >
                            {isPositive ? "+" : ""}
                            {fmt(Math.round(s.balance))}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 11,
                            color: C.textDim,
                          }}
                        >
                          <span>Pagado: {fmt(s.pagado)}</span>
                          <span
                            style={S.pill(
                              isPositive ? C.profitLight : C.lossLight,
                              isPositive ? C.profit : C.loss
                            )}
                          >
                            {isPositive
                              ? "A favor"
                              : "Debe " + fmt(Math.abs(Math.round(s.balance)))}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {siblings.length === 0 && (
                  <div style={{ padding: 16, color: C.textDim, fontSize: 14 }}>
                    Sin hermanos registrados
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Horario de hoy</div>
        <div style={S.card}>
          {(() => {
            const schedule = {};
            meds
              .filter((m) => m.active)
              .forEach((m) => {
                (m.times || []).forEach((t) => {
                  if (!schedule[t]) schedule[t] = [];
                  schedule[t].push(m);
                });
              });
            const sorted = Object.entries(schedule).sort((a, b) =>
              a[0].localeCompare(b[0])
            );
            if (sorted.length === 0)
              return (
                <div style={{ padding: 16, color: C.textDim, fontSize: 14 }}>
                  Sin medicamentos activos
                </div>
              );
            return sorted.map(([time, ms]) => (
              <div key={time} style={S.listItem}>
                <span
                  style={{
                    fontFamily: mono,
                    fontSize: 15,
                    fontWeight: 700,
                    color: C.accent,
                    minWidth: 50,
                  }}
                >
                  {time}
                </span>
                <div style={{ flex: 1 }}>
                  {ms.map((m) => (
                    <div key={m.id} style={{ fontSize: 13 }}>
                      <strong>{m.name}</strong>{" "}
                      <span style={{ color: C.textSec }}>{m.dose}</span>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Gastos recientes</div>
        <div style={S.card}>
          {expenses.slice(0, 5).map((e) => {
            const cc = catColors[e.category] || catColors.Otro;
            const sib = e.paid_by
              ? siblings.find((s) => s.id === e.paid_by)
              : null;
            return (
              <div key={e.id} style={S.listItem}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}
                  >
                    {e.concept}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={S.pill(cc.bg, cc.text)}>{e.category}</span>
                    {e.source === "directo" && sib && (
                      <span style={{ fontSize: 12, color: C.textSec }}>
                        → {sib.name}
                      </span>
                    )}
                    {e.source === "caja" && (
                      <span style={{ fontSize: 12, color: C.teal }}>
                        Caja chica
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div
                    style={{ fontFamily: mono, fontSize: 15, fontWeight: 700 }}
                  >
                    {fmt(e.amount)}
                  </div>
                  <div style={{ fontSize: 11, color: C.textDim }}>
                    {fmtDate(e.date)}
                  </div>
                </div>
              </div>
            );
          })}
          {expenses.length === 0 && (
            <div style={{ padding: 16, color: C.textDim, fontSize: 14 }}>
              Sin gastos registrados
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Medications Tab ────────────────────────────────────────────
function MedsTab({ meds, isAdmin, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: "",
    dose: "",
    frequency: "",
    doctor: "",
    category: "",
    stock: "",
    min_stock: "10",
    times: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const schedule = useMemo(() => {
    const map = {};
    meds
      .filter((m) => m.active)
      .forEach((m) => {
        (m.times || []).forEach((t) => {
          if (!map[t]) map[t] = [];
          map[t].push(m);
        });
      });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [meds]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const timesArr = form.times
        .split(",")
        .map((t) => {
          const trimmed = t.trim();
          if (!trimmed) return null;
          const parts = trimmed.split(":");
          if (parts.length === 2) {
            return parts[0].padStart(2, "0") + ":" + parts[1].padStart(2, "0");
          }
          return trimmed;
        })
        .filter(Boolean);
      const payload = {
        name: form.name,
        dose: form.dose,
        frequency: form.frequency,
        doctor: form.doctor || null,
        category: form.category || null,
        stock: parseInt(form.stock) || 0,
        min_stock: parseInt(form.min_stock) || 10,
        times: timesArr,
        notes: form.notes || null,
        active: true,
      };
      if (editId) {
        await sb.update("medications", editId, payload);
      } else {
        await sb.insert("medications", payload);
      }
      setShowAdd(false);
      setEditId(null);
      setForm({
        name: "",
        dose: "",
        frequency: "",
        doctor: "",
        category: "",
        stock: "",
        min_stock: "10",
        times: "",
        notes: "",
      });
      onRefresh();
    } catch (e) {
      alert("Error: " + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este medicamento?")) return;
    try {
      await sb.delete("medications", id);
      setShowAdd(false);
      setEditId(null);
      onRefresh();
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      name: m.name,
      dose: m.dose,
      frequency: m.frequency,
      doctor: m.doctor || "",
      category: m.category || "",
      stock: String(m.stock),
      min_stock: String(m.min_stock),
      times: (m.times || []).join(", "),
      notes: m.notes || "",
    });
    setShowAdd(true);
  };

  return (
    <div style={S.content}>
      <div style={S.section}>
        <div style={S.sectionTitle}>Horario del día</div>
        {schedule.map(([time, ms]) => (
          <div key={time} style={{ ...S.card, marginBottom: 8 }}>
            <div
              style={{
                padding: "10px 16px 8px",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontFamily: mono,
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.accent,
                }}
              >
                {time}
              </span>
              <span style={S.pill(C.surface2, C.textSec)}>
                {ms.length} {ms.length === 1 ? "med" : "meds"}
              </span>
            </div>
            {ms.map((m) => (
              <div
                key={m.id}
                style={{
                  ...S.listItem,
                  cursor: isAdmin ? "pointer" : "default",
                }}
                onClick={() => isAdmin && openEdit(m)}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>
                    {m.name}
                  </span>
                  <span
                    style={{ fontSize: 13, color: C.textSec, marginLeft: 6 }}
                  >
                    {m.dose}
                  </span>
                  <div style={{ fontSize: 12, color: C.textDim }}>
                    {m.category || m.frequency}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontFamily: mono,
                      fontSize: 14,
                      fontWeight: 700,
                      color: m.stock <= m.min_stock ? C.loss : C.profit,
                    }}
                  >
                    {m.stock}
                  </div>
                  <div style={S.stockBar}>
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(
                          (m.stock / (m.min_stock * 3)) * 100,
                          100
                        )}%`,
                        borderRadius: 3,
                        background:
                          m.stock <= m.min_stock
                            ? C.loss
                            : m.stock <= m.min_stock * 1.5
                            ? C.warn
                            : C.profit,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
        {schedule.length === 0 && (
          <div
            style={{
              ...S.card,
              padding: 20,
              textAlign: "center",
              color: C.textDim,
            }}
          >
            Sin medicamentos. Agrega el primero con el botón +
          </div>
        )}
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Todos los medicamentos</div>
        <div style={S.card}>
          {meds.map((m) => (
            <div
              key={m.id}
              style={{
                ...S.listItem,
                cursor: isAdmin ? "pointer" : "default",
                opacity: m.active ? 1 : 0.5,
              }}
              onClick={() => isAdmin && openEdit(m)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600 }}>
                    {m.name}
                  </span>
                  <span style={{ fontSize: 12, color: C.textSec }}>
                    {m.dose}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0,
                    marginBottom: 4,
                  }}
                >
                  {(m.times || []).map((t) => (
                    <span key={t} style={S.timeChip}>
                      {t}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: C.textDim }}>
                  {m.frequency} · {m.doctor || "Sin doctor"}
                </div>
                {m.notes && (
                  <div
                    style={{
                      fontSize: 12,
                      color: C.accent,
                      marginTop: 3,
                      fontStyle: "italic",
                    }}
                  >
                    {m.notes}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                  style={{
                    fontFamily: mono,
                    fontSize: 14,
                    fontWeight: 700,
                    color: m.stock <= m.min_stock ? C.loss : C.profit,
                  }}
                >
                  {m.stock}
                </div>
                <div style={{ fontSize: 10, color: C.textDim }}>
                  min {m.min_stock}
                </div>
              </div>
            </div>
          ))}
          {meds.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: C.textDim }}>
              Sin medicamentos registrados
            </div>
          )}
        </div>
      </div>

      {isAdmin && (
        <FAB
          onClick={() => {
            setEditId(null);
            setForm({
              name: "",
              dose: "",
              frequency: "",
              doctor: "",
              category: "",
              stock: "",
              min_stock: "10",
              times: "",
              notes: "",
            });
            setShowAdd(true);
          }}
        />
      )}

      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title={editId ? "Editar medicamento" : "Nuevo medicamento"}
      >
        <div style={S.formGroup}>
          <label style={S.label}>Nombre *</label>
          <input
            style={S.input}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ej: Losartán"
          />
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div style={S.formGroup}>
            <label style={S.label}>Dosis *</label>
            <input
              style={S.input}
              value={form.dose}
              onChange={(e) => setForm({ ...form, dose: e.target.value })}
              placeholder="Ej: 50mg"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Frecuencia *</label>
            <input
              style={S.input}
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              placeholder="Ej: Cada 12h"
            />
          </div>
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Horarios (separados por coma)</label>
          <input
            style={S.input}
            value={form.times}
            onChange={(e) => setForm({ ...form, times: e.target.value })}
            placeholder="Ej: 08:00, 20:00"
          />
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div style={S.formGroup}>
            <label style={S.label}>Doctor</label>
            <input
              style={S.input}
              value={form.doctor}
              onChange={(e) => setForm({ ...form, doctor: e.target.value })}
              placeholder="Dr. ..."
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Categoría</label>
            <input
              style={S.input}
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Ej: Hipertensión"
            />
          </div>
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div style={S.formGroup}>
            <label style={S.label}>Stock actual</label>
            <input
              style={S.input}
              type="number"
              value={form.stock}
              onChange={(e) => setForm({ ...form, stock: e.target.value })}
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Stock mínimo</label>
            <input
              style={S.input}
              type="number"
              value={form.min_stock}
              onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
            />
          </div>
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Notas</label>
          <input
            style={S.input}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Notas adicionales..."
          />
        </div>
        <button
          style={S.btn(C.accent, "#fff")}
          onClick={handleSave}
          disabled={saving || !form.name || !form.dose || !form.frequency}
        >
          {saving
            ? "Guardando..."
            : editId
            ? "Actualizar"
            : "Agregar medicamento"}
        </button>
        {editId && (
          <button
            style={{
              ...S.btn("transparent", C.loss),
              marginTop: 8,
              border: `1px solid ${C.loss}20`,
            }}
            onClick={() => handleDelete(editId)}
          >
            Eliminar medicamento
          </button>
        )}
      </Modal>
    </div>
  );
}

// ─── Expenses Tab ───────────────────────────────────────────────
function ExpensesTab({ expenses, siblings, isAdmin, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState("todos");
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    date: "",
    concept: "",
    category: "Consulta",
    amount: "",
    doctor: "",
    source: "caja",
    paid_by: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const totalAll = expenses.reduce((s, e) => s + Number(e.amount), 0);

  const byCategory = useMemo(() => {
    const map = {};
    expenses.forEach((e) => {
      map[e.category] = (map[e.category] || 0) + Number(e.amount);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

  const filtered =
    filter === "todos"
      ? expenses
      : filter === "caja"
      ? expenses.filter((e) => e.source === "caja")
      : expenses.filter((e) => e.source === "directo");

  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      date: e.date,
      concept: e.concept,
      category: e.category,
      amount: String(e.amount),
      doctor: e.doctor || "",
      source: e.source,
      paid_by: e.paid_by || "",
      notes: e.notes || "",
    });
    setShowAdd(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        date: form.date || new Date().toISOString().split("T")[0],
        concept: form.concept,
        category: form.category,
        amount: parseFloat(form.amount),
        doctor: form.doctor || null,
        source: form.source,
        paid_by:
          form.source === "directo" && form.paid_by ? form.paid_by : null,
        notes: form.notes || null,
      };
      if (editId) {
        await sb.update("expenses", editId, payload);
      } else {
        await sb.insert("expenses", payload);
        if (form.source === "caja") {
          await sb.insert("caja_movements", {
            date: payload.date,
            type: "salida",
            amount: payload.amount,
            note: payload.concept,
          });
        }
      }
      setShowAdd(false);
      setEditId(null);
      setForm({
        date: "",
        concept: "",
        category: "Consulta",
        amount: "",
        doctor: "",
        source: "caja",
        paid_by: "",
        notes: "",
      });
      onRefresh();
    } catch (e) {
      alert("Error: " + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try {
      await sb.delete("expenses", id);
      setShowAdd(false);
      setEditId(null);
      onRefresh();
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  return (
    <div style={S.content}>
      <div style={S.kpiGrid}>
        <KPI
          label="Total gastado"
          value={fmt(totalAll)}
          color={C.accent}
          bgColor={C.accentLight}
        />
        <KPI
          label="Registros"
          value={expenses.length}
          sub="Todos los gastos"
          color={C.purple}
          bgColor={C.purpleLight}
        />
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Por categoría</div>
        <div style={S.card}>
          {byCategory.map(([cat, amt]) => {
            const cc = catColors[cat] || catColors.Otro;
            const pct = totalAll > 0 ? Math.round((amt / totalAll) * 100) : 0;
            return (
              <div key={cat} style={{ ...S.listItem, gap: 10 }}>
                <span style={S.pill(cc.bg, cc.text)}>{cat}</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      height: 5,
                      borderRadius: 3,
                      background: C.surface2,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${pct}%`,
                        borderRadius: 3,
                        background: cc.text,
                      }}
                    />
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div
                    style={{ fontFamily: mono, fontSize: 13, fontWeight: 700 }}
                  >
                    {fmt(amt)}
                  </div>
                  <div style={{ fontSize: 11, color: C.textDim }}>{pct}%</div>
                </div>
              </div>
            );
          })}
          {byCategory.length === 0 && (
            <div style={{ padding: 16, color: C.textDim }}>Sin gastos</div>
          )}
        </div>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Historial</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[
            ["todos", "Todos"],
            ["caja", "Caja chica"],
            ["directo", "Directo"],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              style={{
                padding: "7px 14px",
                borderRadius: 20,
                border: "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: filter === val ? 600 : 400,
                fontFamily: font,
                background: filter === val ? C.accent : C.surface2,
                color: filter === val ? "#fff" : C.textSec,
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={S.card}>
          {filtered.map((e) => {
            const cc = catColors[e.category] || catColors.Otro;
            const sib = e.paid_by
              ? siblings.find((s) => s.id === e.paid_by)
              : null;
            return (
              <div
                key={e.id}
                style={{
                  ...S.listItem,
                  cursor: isAdmin ? "pointer" : "default",
                }}
                onClick={() => isAdmin && openEdit(e)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}
                  >
                    {e.concept}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={S.pill(cc.bg, cc.text)}>{e.category}</span>
                    {e.source === "directo" && sib && (
                      <span style={{ fontSize: 12, color: C.textSec }}>
                        → {sib.name}
                      </span>
                    )}
                    {e.source === "caja" && (
                      <span style={{ fontSize: 12, color: C.teal }}>
                        Caja chica
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div
                    style={{ fontFamily: mono, fontSize: 15, fontWeight: 700 }}
                  >
                    {fmt(e.amount)}
                  </div>
                  <div style={{ fontSize: 11, color: C.textDim }}>
                    {fmtDate(e.date)}
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: 16, color: C.textDim }}>Sin gastos</div>
          )}
        </div>
      </div>

      {isAdmin && (
        <FAB
          onClick={() => {
            setEditId(null);
            setForm({
              date: "",
              concept: "",
              category: "Consulta",
              amount: "",
              doctor: "",
              source: "caja",
              paid_by: "",
              notes: "",
            });
            setShowAdd(true);
          }}
        />
      )}

      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title={editId ? "Editar gasto" : "Nuevo gasto"}
      >
        <div style={S.formGroup}>
          <label style={S.label}>Concepto *</label>
          <input
            style={S.input}
            value={form.concept}
            onChange={(e) => setForm({ ...form, concept: e.target.value })}
            placeholder="Ej: Consulta cardiología"
          />
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div style={S.formGroup}>
            <label style={S.label}>Monto *</label>
            <input
              style={S.input}
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="$0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Fecha</label>
            <input
              style={S.input}
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div style={S.formGroup}>
            <label style={S.label}>Categoría</label>
            <select
              style={S.select}
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {Object.keys(catColors).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Origen del pago</label>
            <select
              style={S.select}
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
            >
              <option value="caja">Caja chica</option>
              <option value="directo">Directo (hermano)</option>
            </select>
          </div>
        </div>
        {form.source === "directo" && (
          <div style={S.formGroup}>
            <label style={S.label}>¿Quién paga?</label>
            <select
              style={S.select}
              value={form.paid_by}
              onChange={(e) => setForm({ ...form, paid_by: e.target.value })}
            >
              <option value="">Seleccionar...</option>
              {siblings.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={S.formGroup}>
          <label style={S.label}>Doctor</label>
          <input
            style={S.input}
            value={form.doctor}
            onChange={(e) => setForm({ ...form, doctor: e.target.value })}
            placeholder="Dr. ..."
          />
        </div>
        <button
          style={S.btn(C.accent, "#fff")}
          onClick={handleSave}
          disabled={saving || !form.concept || !form.amount}
        >
          {saving
            ? "Guardando..."
            : editId
            ? "Actualizar gasto"
            : "Registrar gasto"}
        </button>
        {editId && (
          <button
            style={{
              ...S.btn("transparent", C.loss),
              marginTop: 8,
              border: `1px solid ${C.loss}20`,
            }}
            onClick={() => handleDelete(editId)}
          >
            Eliminar gasto
          </button>
        )}
      </Modal>
    </div>
  );
}

// ─── Caja Chica Tab ─────────────────────────────────────────────
function CajaTab({ caja, siblings, isAdmin, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    type: "entrada",
    amount: "",
    sibling_id: "",
    note: "",
    date: "",
  });
  const [saving, setSaving] = useState(false);

  const saldo = caja.reduce(
    (s, m) =>
      m.type === "entrada" ? s + Number(m.amount) : s - Number(m.amount),
    0
  );
  const totalIn = caja
    .filter((m) => m.type === "entrada")
    .reduce((s, m) => s + Number(m.amount), 0);
  const totalOut = caja
    .filter((m) => m.type === "salida")
    .reduce((s, m) => s + Number(m.amount), 0);

  const bySibling = useMemo(() => {
    const map = {};
    siblings.forEach((s) => {
      map[s.id] = { name: s.name, color: s.color, total: 0 };
    });
    caja
      .filter((m) => m.type === "entrada" && m.sibling_id)
      .forEach((m) => {
        if (map[m.sibling_id]) map[m.sibling_id].total += Number(m.amount);
      });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [caja, siblings]);

  const maxContrib = Math.max(...bySibling.map((s) => s.total), 1);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        date: form.date || new Date().toISOString().split("T")[0],
        type: form.type,
        amount: parseFloat(form.amount),
        sibling_id:
          form.type === "entrada" && form.sibling_id ? form.sibling_id : null,
        note: form.note || null,
      };
      await sb.insert("caja_movements", payload);
      setShowAdd(false);
      setForm({
        type: "entrada",
        amount: "",
        sibling_id: "",
        note: "",
        date: "",
      });
      onRefresh();
    } catch (e) {
      alert("Error: " + e.message);
    }
    setSaving(false);
  };

  const handleDeleteMov = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    try {
      await sb.delete("caja_movements", id);
      onRefresh();
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  return (
    <div style={S.content}>
      <div
        style={{
          background: C.accent,
          borderRadius: 18,
          padding: "20px 18px",
          marginBottom: 16,
          color: "#fff",
        }}
      >
        <div
          style={{
            fontSize: 12,
            opacity: 0.7,
            fontWeight: 500,
            marginBottom: 4,
          }}
        >
          Saldo disponible
        </div>
        <div
          style={{
            fontSize: 32,
            fontWeight: 700,
            fontFamily: mono,
            letterSpacing: "-0.03em",
          }}
        >
          {fmt(saldo)}
        </div>
        <div style={{ display: "flex", gap: 20, marginTop: 12, fontSize: 13 }}>
          <div>
            <span style={{ opacity: 0.6 }}>Entradas </span>
            <span style={{ fontFamily: mono, fontWeight: 600 }}>
              +{fmt(totalIn)}
            </span>
          </div>
          <div>
            <span style={{ opacity: 0.6 }}>Salidas </span>
            <span style={{ fontFamily: mono, fontWeight: 600 }}>
              -{fmt(totalOut)}
            </span>
          </div>
        </div>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Aportaciones por hermano</div>
        <div style={S.card}>
          {bySibling.map((s) => (
            <div key={s.name} style={S.listItem}>
              <div style={S.avatar(s.color, 34)}>{s.name[0]}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600 }}>
                    {s.name}
                  </span>
                  <span
                    style={{
                      fontFamily: mono,
                      fontSize: 14,
                      fontWeight: 700,
                      color: s.total > 0 ? C.text : C.textDim,
                    }}
                  >
                    {fmt(s.total)}
                  </span>
                </div>
                <div
                  style={{
                    height: 5,
                    borderRadius: 3,
                    background: C.surface2,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${(s.total / maxContrib) * 100}%`,
                      borderRadius: 3,
                      background: s.color,
                      transition: "width 0.3s",
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Movimientos</div>
        <div style={S.card}>
          {caja.map((m) => {
            const sib = m.sibling_id
              ? siblings.find((s) => s.id === m.sibling_id)
              : null;
            return (
              <div key={m.id} style={S.listItem}>
                <div style={S.movIcon(m.type)}>
                  {m.type === "entrada" ? "↓" : "↑"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {m.note || (m.type === "entrada" ? "Aportación" : "Gasto")}
                  </div>
                  <div style={{ fontSize: 12, color: C.textDim }}>
                    {fmtDate(m.date)}
                    {sib ? ` · ${sib.name}` : ""}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontFamily: mono,
                      fontSize: 15,
                      fontWeight: 700,
                      color: m.type === "entrada" ? C.profit : C.loss,
                    }}
                  >
                    {m.type === "entrada" ? "+" : "-"}
                    {fmt(m.amount)}
                  </span>
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteMov(m.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: C.textDim,
                        fontSize: 16,
                        cursor: "pointer",
                        padding: "2px 4px",
                        lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {caja.length === 0 && (
            <div style={{ padding: 16, color: C.textDim }}>Sin movimientos</div>
          )}
        </div>
      </div>

      {isAdmin && <FAB onClick={() => setShowAdd(true)} />}

      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title="Nuevo movimiento"
      >
        <div style={S.formGroup}>
          <label style={S.label}>Tipo</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              ["entrada", "Aportación", C.profit],
              ["salida", "Gasto", C.loss],
            ].map(([val, label, color]) => (
              <button
                key={val}
                onClick={() => setForm({ ...form, type: val })}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: 10,
                  border:
                    form.type === val
                      ? `2px solid ${color}`
                      : `1px solid ${C.border}`,
                  background: form.type === val ? color + "10" : C.surface,
                  color: form.type === val ? color : C.textSec,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: font,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <div style={S.formGroup}>
            <label style={S.label}>Monto *</label>
            <input
              style={S.input}
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="$0"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Fecha</label>
            <input
              style={S.input}
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
        </div>
        {form.type === "entrada" && (
          <div style={S.formGroup}>
            <label style={S.label}>¿Quién aporta?</label>
            <select
              style={S.select}
              value={form.sibling_id}
              onChange={(e) => setForm({ ...form, sibling_id: e.target.value })}
            >
              <option value="">Seleccionar...</option>
              {siblings.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={S.formGroup}>
          <label style={S.label}>Nota</label>
          <input
            style={S.input}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Ej: Aportación marzo"
          />
        </div>
        <button
          style={S.btn(form.type === "entrada" ? C.profit : C.loss, "#fff")}
          onClick={handleSave}
          disabled={saving || !form.amount}
        >
          {saving
            ? "Guardando..."
            : form.type === "entrada"
            ? "Registrar aportación"
            : "Registrar gasto"}
        </button>
      </Modal>
    </div>
  );
}

// ─── Settings (Edit Siblings) ───────────────────────────────────
function SettingsPanel({ siblings, isAdmin, onRefresh, onClose }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ name: s.name, phone: s.phone || "" });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ name: "", phone: "" });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await sb.update("siblings", editing, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
      });
      setEditing(null);
      setForm({ name: "", phone: "" });
      onRefresh();
    } catch (e) {
      alert("Error: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={S.content}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 700 }}>Configuración</div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: 16,
            color: C.accent,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: font,
          }}
        >
          Cerrar
        </button>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>Hermanos</div>
        <div style={S.card}>
          {siblings.map((s) => (
            <div
              key={s.id}
              style={{
                ...S.listItem,
                flexDirection: editing === s.id ? "column" : "row",
                alignItems: editing === s.id ? "stretch" : "center",
                gap: editing === s.id ? 10 : 12,
              }}
            >
              {editing === s.id ? (
                <>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      style={{ ...S.input, flex: 1 }}
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                      placeholder="Nombre"
                      autoFocus
                    />
                    <input
                      style={{ ...S.input, flex: 1 }}
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      placeholder="Teléfono"
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={handleSave}
                      disabled={saving || !form.name.trim()}
                      style={{
                        ...S.btn(C.accent, "#fff"),
                        padding: "10px",
                        fontSize: 13,
                      }}
                    >
                      {saving ? "..." : "Guardar"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      style={{
                        ...S.btn(C.surface2, C.textSec),
                        padding: "10px",
                        fontSize: 13,
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ ...S.avatar(s.color, 36), fontSize: 14 }}>
                    {s.name[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      {s.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: C.textDim,
                        fontFamily: mono,
                      }}
                    >
                      {s.phone || "Sin teléfono"}
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => startEdit(s)}
                      style={{
                        background: "none",
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontSize: 12,
                        color: C.accent,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      Editar
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...S.card, padding: "14px 16px", background: C.surface2 }}>
        <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.5 }}>
          Admin: {isAdmin ? "Rolando" : "Solo lectura"}
          <br />
          Versión 1.0 · cuidandoasiria.com
        </div>
      </div>
    </div>
  );
}

// ─── Main App ───────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("overview");
  const [showSettings, setShowSettings] = useState(false);
  const [meds, setMeds] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [caja, setCaja] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user && user.id === ADMIN_UID;

  const loadData = useCallback(async () => {
    try {
      const [m, e, c, s] = await Promise.all([
        sb.selectOrder("medications", "", "active.desc,created_at.desc"),
        sb.select("expenses"),
        sb.select("caja_movements"),
        sb.selectOrder("siblings", "", "sort_order.asc"),
      ]);
      setMeds(m);
      setExpenses(e);
      setCaja(c);
      setSiblings(s);
    } catch (err) {
      console.error("Load error:", err);
    }
    setLoading(false);
  }, []);

  const handleLogin = (data) => {
    setUser(data.user);
    loadData();
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  // Scroll to top when switching tabs
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab, showSettings]);

  if (!user) return <LoginScreen onLogin={handleLogin} />;
  if (loading)
    return (
      <div
        style={{
          ...S.page,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <div style={{ color: C.textDim }}>Cargando...</div>
      </div>
    );

  const tabs = [
    { id: "overview", icon: "◉", label: "Inicio" },
    { id: "meds", icon: "✚", label: "Medicinas" },
    { id: "expenses", icon: "◈", label: "Gastos" },
    { id: "caja", icon: "◐", label: "Caja chica" },
  ];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.logo}>
          <div style={S.logoIcon}>S</div>
          <div>
            <div style={S.logoText}>cuidandoasiria</div>
            <div style={S.logoSub}>Cuidando a Siria</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={S.badge(isAdmin)}>{isAdmin ? "Admin" : "Lectura"}</span>
          {isAdmin && (
            <button
              onClick={() => {
                setShowSettings(!showSettings);
                if (showSettings) setTab("overview");
              }}
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: showSettings ? C.accent : C.textDim,
                padding: 4,
              }}
            >
              ⚙
            </button>
          )}
        </div>
      </div>

      {showSettings ? (
        <SettingsPanel
          siblings={siblings}
          isAdmin={isAdmin}
          onRefresh={loadData}
          onClose={() => setShowSettings(false)}
        />
      ) : (
        <>
          {tab === "overview" && (
            <OverviewTab
              meds={meds}
              expenses={expenses}
              caja={caja}
              siblings={siblings}
            />
          )}
          {tab === "meds" && (
            <MedsTab meds={meds} isAdmin={isAdmin} onRefresh={loadData} />
          )}
          {tab === "expenses" && (
            <ExpensesTab
              expenses={expenses}
              siblings={siblings}
              isAdmin={isAdmin}
              onRefresh={loadData}
            />
          )}
          {tab === "caja" && (
            <CajaTab
              caja={caja}
              siblings={siblings}
              isAdmin={isAdmin}
              onRefresh={loadData}
            />
          )}
        </>
      )}

      {!showSettings && (
        <div style={S.bottomNav}>
          {tabs.map((t) => (
            <button
              key={t.id}
              style={S.navBtn(tab === t.id)}
              onClick={() => setTab(t.id)}
            >
              <span style={S.navIcon}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
